import * as React from 'react';
import * as JSZip from 'jszip';

// Ensure these filenames match exactly what is in public/sourcebooks/
interface SourceBookProp {
    name: string;
    filename: string;
}

const SOURCEBOOKS: SourceBookProp[] = [
    { name: "Atmor", filename: "Atmor.ds-sourcebook" },
    { name: "Atmor Titles", filename: "Atmor Titles.ds-sourcebook" },
    { name: "Atmor Kits", filename: "Atmor Kits.ds-sourcebook" },
    { name: "Atmor Items", filename: "Atmor Items.ds-sourcebook"} ,
    { name: "Conduit Alternate", filename: "Conduit Alternate.ds-sourcebook" },
    { name: "Expertise Perks", filename: "Expertise Perks.ds-sourcebook" },
];

export function DrawSteelSourcebooks(): JSX.Element {
    const [fileSizes, setFileSizes] = React.useState<Record<string, string>>({});
    const [selectedFiles, setSelectedFiles] = React.useState<Set<string>>(new Set());

    React.useEffect(() => {
        SOURCEBOOKS.forEach((book) => {
            fetch(`/sourcebooks/${book.filename}`, { method: 'HEAD' })
                .then((res) => {
                    const length = res.headers.get('Content-Length');
                    if (length) {
                        const bytes = parseInt(length, 10);
                        const k = 1024;
                        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
                        const i = bytes === 0 ? 0 : Math.floor(Math.log(bytes) / Math.log(k));
                        const size = `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
                        setFileSizes((prev) => ({ ...prev, [book.filename]: size }));
                    }
                })
                .catch(console.error);
        });
    }, []);

    const handleDownloadSelected = async () => {
        if (selectedFiles.size === 0) return;

        const filesToDownload = SOURCEBOOKS.filter(book => selectedFiles.has(book.filename));

        if (filesToDownload.length === 1) {
            const book = filesToDownload[0];
            const link = document.createElement('a');
            link.href = `/sourcebooks/${book.filename}`;
            link.download = book.filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            return;
        }

        const zip = new JSZip();
        const promises = filesToDownload.map(async (book) => {
            const response = await fetch(`/sourcebooks/${book.filename}`);
            const blob = await response.blob();
            zip.file(book.filename, blob);
        });

        await Promise.all(promises);

        const content = await zip.generateAsync({ type: "blob" });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(content);
        link.download = "DrawSteelSourcebooks.zip";
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const toggleSelection = (filename: string) => {
        const newSelection = new Set(selectedFiles);
        if (newSelection.has(filename)) {
            newSelection.delete(filename);
        } else {
            newSelection.add(filename);
        }
        setSelectedFiles(newSelection);
    };

    const toggleSelectAll = () => {
        if (selectedFiles.size === SOURCEBOOKS.length) {
            setSelectedFiles(new Set());
        } else {
            setSelectedFiles(new Set(SOURCEBOOKS.map(b => b.filename)));
        }
    };

    return (
        <div className="flex-vertical gap-2 ma-2 flex-fill">
            <h2 className="white-text">Draw Steel Sourcebooks</h2>
            <div className="flex-vertical gap-2">
                <div className="flex gap-2 align-center">
                    <div className="flex gap-1 item-align-bottom ml-2">
                        <input
                            type="checkbox"
                            checked={selectedFiles.size === SOURCEBOOKS.length && SOURCEBOOKS.length > 0}
                            onChange={toggleSelectAll}
                            style={{ cursor: 'pointer', width: '20px', height: '20px' }}
                        />
                        <span className="white-text">Select All</span>
                    </div>
                </div>
                {SOURCEBOOKS.map((book) => (
                    <div key={book.filename} className="flex basic-box gap-2">
                        <input
                            type="checkbox"
                            checked={selectedFiles.has(book.filename)}
                            onChange={() => toggleSelection(book.filename)}
                            className='cursor-pointer'
                            style={{ width: '20px', height: '20px' }}
                        />
                        <div className="flex-fill white-text flex justify-between item-align-bottom">
                            <span>{book.name}</span>

                            {fileSizes[book.filename] && (
                                <span>{fileSizes[book.filename]}</span>
                            )}
                        </div>
                    </div>
                ))}
                <div className="flex gap-2 align-center">
                    <button onClick={handleDownloadSelected} style={{ width: 'fit-content' }} disabled={selectedFiles.size === 0}>
                        Download Selected
                    </button>
                </div>
            </div>
        </div>
    );
}
